import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import api from '../lib/api';
import { toast } from 'sonner';
import { ArrowLeft, Edit, Trash2, PackageCheck, Download, Package, Truck, FileText, Camera, History, Loader2 } from 'lucide-react';
import { formatNumber, formatDate } from '../lib/utils';
import { GreyReceiptForm } from '../components/forms';
import { useSettings } from '../contexts/SettingsContext';
import { exportReceiptToPDF } from '../lib/pdf-export';
import { usePermissions } from '../contexts/PermissionsContext';

const ReceiptDetails = () => {
  const params = useParams();
  const receiptId = params['*'] || params.receiptId;
  const navigate = useNavigate();
  const { controls } = useSettings();
  const { hasModulePermission } = usePermissions();
  const [receipt, setReceipt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [bills, setBills] = useState([]);
  const [lrs, setLRs] = useState([]);
  const [linkedInfo, setLinkedInfo] = useState(null);
  const [lrHistory, setLrHistory] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, [receiptId]);

  const fetchData = async () => {
    try {
      const receiptRes = await api.get(`/grey-receipts/${receiptId}`);
      setReceipt(receiptRes.data);
      
      // Fetch bills, LRs, and receipt history in parallel
      const fetches = [
        receiptRes.data.order_id ? api.get(`/grey-bills?order_id=${receiptRes.data.order_id}`) : Promise.resolve({ data: [] }),
        receiptRes.data.order_id ? api.get(`/lr-reports?order_id=${receiptRes.data.order_id}`) : Promise.resolve({ data: [] }),
        api.get(`/grey-receipts/${receiptId}/lr-history`),
      ];
      
      if (receiptRes.data.lr_id) {
        fetches.push(api.get(`/lr-reports/${receiptRes.data.lr_id}/autofill-data`));
      }
      
      const [billsRes, lrsRes, historyRes, lrDataRes] = await Promise.allSettled(fetches);
      setBills(billsRes.status === 'fulfilled' ? billsRes.value.data : []);
      setLRs(lrsRes.status === 'fulfilled' ? lrsRes.value.data : []);
      setLrHistory(historyRes.status === 'fulfilled' ? historyRes.value.data : []);
      
      if (lrDataRes?.status === 'fulfilled' && lrDataRes.value?.data?.suggested_values) {
        const sv = lrDataRes.value.data.suggested_values;
        setLinkedInfo({
          order_number: sv.order_number,
          bill_number: sv.bill_number,
          lr_number: sv.lr_number,
          lr_number_transport: lrDataRes.value.data.lr?.lr_number_transport,
          transport_name: sv.transport_name,
          bales_in_lr: sv.bales_in_lr,
          item_name: sv.item_name,
          supplier_name: sv.supplier_name,
        });
      }
      
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load receipt details');
      setLoading(false);
    }
  };

  const handleEditSuccess = () => {
    setIsEditing(false);
    fetchData();
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/grey-receipts/${receiptId}`);
      toast.success('Receipt deleted successfully!');
      navigate('/grey-receipts');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete receipt');
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post(`/grey-receipts/${receiptId}/upload-gate-pass-photo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Gate pass photo uploaded');
      fetchData();
    } catch (error) {
      toast.error('Failed to upload photo');
    }
    setUploadingPhoto(false);
  };

  const gateStatusColors = {
    'Received': 'bg-green-50 text-green-700 border-green-200',
    'Partially Received': 'bg-amber-50 text-amber-700 border-amber-200',
    'Approved': 'bg-green-50 text-green-700 border-green-200',
    'Pending Inspection': 'bg-amber-50 text-amber-700 border-amber-200',
    'Rejected': 'bg-red-50 text-red-700 border-red-200',
    'Conditional': 'bg-blue-50 text-blue-700 border-blue-200'
  };

  const API_URL = process.env.REACT_APP_BACKEND_URL || '';

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (!receipt) {
    return (
      <div className="p-8 text-center">
        <PackageCheck className="h-16 w-16 mx-auto mb-4 text-slate-300" />
        <p className="text-slate-600">Receipt not found</p>
      </div>
    );
  }

  const biltyDisplay = receipt.lr_number_transport || receipt.lr_number || '-';
  const lrStatusDisplay = receipt.lr_status || '';

  return (
    <div data-testid="receipt-details-page" className="p-4 md:p-6 lg:p-8 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/grey-receipts')}
          className="mb-4"
          data-testid="back-btn"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Receipts
        </Button>
        
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{receipt.receipt_number}</h1>
            <p className="text-slate-500 mt-1">
              Received on {formatDate(receipt.received_date)}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {controls?.features?.enable_pdf_export !== false && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => exportReceiptToPDF(receipt, linkedInfo)}
                data-testid="export-pdf-btn"
              >
                <Download className="h-4 w-4 mr-2" />
                PDF
              </Button>
            )}
            {hasModulePermission('grey_receipts', 'edit') && (
              <Sheet open={isEditing} onOpenChange={setIsEditing}>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setIsEditing(true)}
                  data-testid="edit-receipt-btn"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
                <SheetContent className="w-full sm:max-w-md overflow-y-auto">
                  <SheetHeader>
                    <SheetTitle>Edit Receipt - {receipt.receipt_number}</SheetTitle>
                  </SheetHeader>
                  <GreyReceiptForm 
                    initialData={{...receipt, order_unit: linkedInfo?.order_unit}}
                    bills={bills}
                    lrs={lrs}
                    onSuccess={handleEditSuccess}
                    onCancel={() => setIsEditing(false)}
                    mode="edit"
                  />
                </SheetContent>
              </Sheet>
            )}
            {hasModulePermission('grey_receipts', 'delete') && (
              <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm" className="text-red-600 hover:bg-red-50" data-testid="delete-receipt-btn">
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Delete Receipt</DialogTitle>
                  </DialogHeader>
                  <p>Are you sure you want to delete <strong>{receipt.receipt_number}</strong>? This will revert the LR status.</p>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
                    <Button variant="destructive" onClick={handleDelete}>Delete</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>
      </div>

      {/* Linked LR Info Banner */}
      {linkedInfo && (
        <Card className="mb-6 border-teal-200 bg-teal-50/50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Truck className="h-5 w-5 text-teal-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="font-semibold text-teal-800 text-sm">
                  Linked LR: {linkedInfo.lr_number_transport || linkedInfo.lr_number}
                  {lrStatusDisplay && <span className="ml-2 text-xs font-normal text-teal-600">({lrStatusDisplay})</span>}
                </p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2 text-sm">
                  <div><span className="text-teal-600">Order:</span> <span className="font-medium">{linkedInfo.order_number}</span></div>
                  <div><span className="text-teal-600">Bill:</span> <span className="font-medium">{linkedInfo.bill_number || '-'}</span></div>
                  <div><span className="text-teal-600">Transport:</span> <span className="font-medium">{linkedInfo.transport_name}</span></div>
                  <div><span className="text-teal-600">Bales in LR:</span> <span className="font-bold">{linkedInfo.bales_in_lr}</span></div>
                  <div><span className="text-teal-600">Item:</span> <span className="font-medium">{linkedInfo.item_name || '-'}</span></div>
                  {linkedInfo.supplier_name && (
                    <div><span className="text-teal-600">Supplier:</span> <span className="font-medium">{linkedInfo.supplier_name}</span></div>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Receipt Details */}
      <Card className="mb-6 border border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PackageCheck className="h-5 w-5" />
            Receipt Details
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            <div>
              <p className="text-sm text-slate-500 mb-1">Receipt Number</p>
              <p className="font-mono font-medium text-slate-900">{receipt.receipt_number}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500 mb-1">LR (Bilty Number)</p>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="font-mono bg-teal-50 text-teal-700 border-teal-200">
                  {biltyDisplay}
                </Badge>
                {lrStatusDisplay && (
                  <span className="text-xs text-slate-500">{lrStatusDisplay}</span>
                )}
              </div>
            </div>
            <div>
              <p className="text-sm text-slate-500 mb-1">Order</p>
              {hasModulePermission('grey_orders', 'view') ? (
                <Badge 
                  variant="outline" 
                  className="cursor-pointer hover:bg-slate-100"
                  onClick={() => navigate(`/grey-orders/${receipt.order_id}`)}
                >
                  {receipt.order_number}
                </Badge>
              ) : (
                <span className="font-medium">{receipt.order_number}</span>
              )}
            </div>
            <div>
              <p className="text-sm text-slate-500 mb-1">Received Quantity</p>
              <p className="text-lg font-bold text-teal-700">{formatNumber(receipt.received_quantity)} <span className="text-sm font-normal text-slate-500">Bales</span></p>
            </div>
            {(receipt.received_material_quantity != null || receipt.material_unit) && (
              <div>
                <p className="text-sm text-slate-500 mb-1">Received Material Quantity</p>
                <p className="text-lg font-bold text-teal-700" data-testid="receipt-material-qty">
                  {receipt.received_material_quantity != null ? formatNumber(receipt.received_material_quantity) : '—'}{' '}
                  <span className="text-sm font-normal text-slate-500">{receipt.material_unit || ''}</span>
                </p>
              </div>
            )}
            <div>
              <p className="text-sm text-slate-500 mb-1">Received Date</p>
              <p className="font-medium">{formatDate(receipt.received_date)}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500 mb-1">Location</p>
              {receipt.location_name ? (
                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                  {receipt.location_name}
                </Badge>
              ) : (
                <span className="text-slate-400">-</span>
              )}
            </div>
            <div>
              <p className="text-sm text-slate-500 mb-1">Verified By</p>
              <p className="font-medium">{receipt.verified_by}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500 mb-1">Gate Status</p>
              <Badge className={gateStatusColors[receipt.gate_status] || gateStatusColors['Received']}>
                {receipt.gate_status}
              </Badge>
            </div>
            <div>
              <p className="text-sm text-slate-500 mb-1">Added By</p>
              <p className="font-medium">{receipt.created_by_name}</p>
            </div>
            {receipt.discrepancy_notes && (
              <div className="col-span-2 md:col-span-3">
                <p className="text-sm text-slate-500 mb-1">Discrepancy Notes</p>
                <p className="font-medium text-red-600">{receipt.discrepancy_notes}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Gate Pass Received Photo */}
      <Card className="mb-6 border border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Camera className="h-4 w-4" />
            Gate Pass Received Photo
          </CardTitle>
        </CardHeader>
        <CardContent>
          {receipt.gate_pass_photo_url ? (
            <div className="space-y-3">
              <img
                src={`${API_URL}${receipt.gate_pass_photo_url}`}
                alt="Gate Pass Received"
                className="max-w-md rounded-lg border border-slate-200 shadow-sm"
                data-testid="gate-pass-photo"
              />
              {hasModulePermission('grey_receipts', 'edit') && (
                <div>
                  <input type="file" ref={fileInputRef} accept="image/*" capture="environment" className="hidden" onChange={handlePhotoUpload} />
                  <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploadingPhoto}>
                    {uploadingPhoto ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Uploading...</> : <><Camera className="h-4 w-4 mr-2" />Replace Photo</>}
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <Camera className="h-10 w-10 mx-auto mb-3 text-slate-300" />
              <p className="text-slate-500 text-sm mb-3">No gate pass photo uploaded</p>
              {hasModulePermission('grey_receipts', 'edit') && (
                <div>
                  <input type="file" ref={fileInputRef} accept="image/*" capture="environment" className="hidden" onChange={handlePhotoUpload} />
                  <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploadingPhoto} data-testid="upload-gate-pass-btn">
                    {uploadingPhoto ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Uploading...</> : <><Camera className="h-4 w-4 mr-2" />Upload Photo</>}
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Receipt History for this LR */}
      {lrHistory.length > 1 && (
        <Card className="mb-6 border border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <History className="h-4 w-4" />
              Receipt History for this LR
              <Badge variant="outline" className="ml-2 text-xs">{lrHistory.length} entries</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {lrHistory.map((entry, idx) => (
                <div
                  key={entry.receipt_id}
                  data-testid={`history-entry-${entry.receipt_id}`}
                  className={`flex items-center gap-4 p-3 rounded-lg border ${
                    entry.receipt_id === receiptId
                      ? 'border-teal-300 bg-teal-50'
                      : 'border-slate-200 bg-slate-50 cursor-pointer hover:bg-slate-100'
                  }`}
                  onClick={() => entry.receipt_id !== receiptId && navigate(`/grey-receipts/${entry.receipt_id}`)}
                >
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-sm font-bold text-slate-600">
                    {idx + 1}
                  </div>
                  <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                    <div>
                      <span className="text-slate-500">Date: </span>
                      <span className="font-medium">{formatDate(entry.received_date)}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Qty: </span>
                      <span className="font-bold text-teal-700">{formatNumber(entry.received_quantity)}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Location: </span>
                      <span className="font-medium">{entry.location_name || '-'}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">By: </span>
                      <span className="font-medium">{entry.created_by_name || entry.verified_by}</span>
                    </div>
                  </div>
                  {entry.receipt_id === receiptId && (
                    <Badge variant="outline" className="bg-teal-100 text-teal-700 text-xs flex-shrink-0">Current</Badge>
                  )}
                </div>
              ))}
              <div className="flex items-center justify-between pt-2 border-t border-slate-200 text-sm">
                <span className="font-medium text-slate-600">Total Received</span>
                <span className="font-bold text-lg text-teal-700">
                  {formatNumber(lrHistory.reduce((sum, e) => sum + e.received_quantity, 0))}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ReceiptDetails;
